import React, { Suspense, lazy } from 'react';
import { Loader2 as Spinner, AlertTriangle } from 'lucide-react';

/**
 * Progressive Loading and Lazy Loading Utility
 * Implements lazy loading for components and progressive enhancement
 */

// Loading fallback component
export const LoadingFallback = ({ message = "Loading...", size = "md" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };

  return (
    <div className="flex items-center justify-center p-8">
      <div className="flex items-center space-x-3 text-gray-600">
        <div className="animate-spin">
          <Spinner className={sizeClasses[size]} />
        </div>
        <span className="text-sm">{message}</span>
      </div>
    </div>
  );
};

// Error boundary for lazy loaded components
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy component error:', error, errorInfo);
  }

  render() {
    const { children, fallback: FallbackComponent } = this.props;
    const { hasError, error } = this.state;
    if (!hasError) return children;

    if (FallbackComponent) {
      return <FallbackComponent error={error} />;
    }

    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to Load Component
          </h3>
          <p className="text-gray-600 mb-4">
            There was an error loading this component. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
}

// Lazy loading wrapper with retry mechanism
const lazyLoadWithRetry = (importFunc, retries = 3) => {
  return lazy(() =>
    new Promise((resolve, reject) => {
      const attemptImport = (remainingRetries) => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            if (remainingRetries > 0) {
              console.warn(`Lazy loading failed, retrying... (${retries - remainingRetries + 1}/${retries})`);
              setTimeout(() => attemptImport(remainingRetries - 1), 1000);
            } else {
              reject(error);
            }
          });
      };

      attemptImport(retries);
    })
  );
};

// Preload critical components
const preloadCriticalComponents = () => {
  // Preload login page (critical for authentication)
  import('../pages/Login');

  // Preload dashboard (critical for main app)
  import('../pages/Dashboard');

  console.log('[LazyLoading] Preloading critical components');
};

// Lazy load all pages with optimized chunking
export const LazyPages = {
  // Authentication
  Login: lazyLoadWithRetry(() => import('../pages/Login')),

  // Main dashboard
  Dashboard: lazyLoadWithRetry(() => import('../pages/Dashboard')),

  // Core functionality
  Scans: lazyLoadWithRetry(() => import('../pages/Scans')),
  NewScan: lazyLoadWithRetry(() => import('../pages/NewScan')),
  ScanDetailsImproved: lazyLoadWithRetry(() => import('../pages/ScanDetailsImproved')),

  // Security features (grouped for better chunking)
  EnhancedSecurityAnalysis: lazyLoadWithRetry(() => import('../pages/EnhancedSecurityAnalysis')),
  ExternalAttackSurface: lazyLoadWithRetry(() => import('../pages/ExternalAttackSurface')),
  AttackSurfaceIssues: lazyLoadWithRetry(() => import('../pages/AttackSurfaceIssues')),
  IntelligenceCoverage: lazyLoadWithRetry(() => import('../pages/IntelligenceCoverage')),
  HttpHeaderSecurity: lazyLoadWithRetry(() => import('../pages/HttpHeaderSecurity')),
  SslSecurity: lazyLoadWithRetry(() => import('../pages/SslSecurity')),
  MainDomains: lazyLoadWithRetry(() => import('../pages/MainDomains')),
  Subdomains: lazyLoadWithRetry(() => import('../pages/Subdomains')),
  CmsSecurity: lazyLoadWithRetry(() => import('../pages/CmsSecurity')),
  IntelligenceHub: lazyLoadWithRetry(() => import('../pages/IntelligenceHub')),

  // Monitoring features
  HackedEmailMonitoring: lazyLoadWithRetry(() => import('../pages/HackedEmailMonitoring')),
  BreachMonitor: lazyLoadWithRetry(() => import('../pages/BreachMonitor')),
  BrandMonitoring: lazyLoadWithRetry(() => import('../pages/BrandMonitoring')),
  SocialPulse: lazyLoadWithRetry(() => import('../pages/SocialPulse')),

  // Admin features (load on demand)
  AdminDashboard: lazyLoadWithRetry(() => import('../pages/AdminDashboard')),
  AdminUsers: lazyLoadWithRetry(() => import('../pages/AdminUsers')),
  AdminScans: lazyLoadWithRetry(() => import('../pages/AdminScans')),
  AdminSmtp: lazyLoadWithRetry(() => import('../pages/AdminSmtp')),
  AdminNotifications: lazyLoadWithRetry(() => import('../pages/AdminNotifications')),
  AdminIntegrations: lazyLoadWithRetry(() => import('../pages/AdminIntegrations')),
  AdminThreatAdvisory: lazyLoadWithRetry(() => import('../pages/AdminThreatAdvisory')),
  AdminTickets: lazyLoadWithRetry(() => import('../pages/AdminTickets')),
  CVEMapSettings: lazyLoadWithRetry(() => import('../pages/CVEMapSettings')),

  // User management
  CreateUser: lazyLoadWithRetry(() => import('../pages/CreateUser')),
  EditUser: lazyLoadWithRetry(() => import('../pages/EditUser')),

  // Other features
  DataExplorer: lazyLoadWithRetry(() => import('../pages/DataExplorer')),
  Reports: lazyLoadWithRetry(() => import('../pages/Reports')),
  UserProfile: lazyLoadWithRetry(() => import('../pages/UserProfile')),
  UserNotifications: lazyLoadWithRetry(() => import('../pages/UserNotifications')),
  UserTickets: lazyLoadWithRetry(() => import('../pages/UserTickets')),
  ThreatAdvisory: lazyLoadWithRetry(() => import('../pages/ThreatAdvisory')),

  // Google Exposure (Google Dorking)
  GoogleExposure: lazyLoadWithRetry(() => import('../pages/GoogleExposure')),

  // Static pages
  Terms: lazyLoadWithRetry(() => import('../pages/Terms')),
  Privacy: lazyLoadWithRetry(() => import('../pages/Privacy')),
  NotFound: lazyLoadWithRetry(() => import('../pages/NotFound'))
};

// Progressive loading wrapper component
export const ProgressiveLoader = ({
  children,
  fallback,
  errorFallback,
  onLoad,
  onError
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (onLoad && isLoaded) {
      onLoad();
    }
  }, [isLoaded, onLoad]);

  React.useEffect(() => {
    if (onError && hasError) {
      onError();
    }
  }, [hasError, onError]);

  return (
    <LazyErrorBoundary
      fallback={errorFallback || (() => setHasError(true))}
    >
      <Suspense
        fallback={
          fallback || (
            <LoadingFallback
              message="Loading component..."
              size="md"
            />
          )
        }
      >
        {React.cloneElement(children, {
          onLoad: () => setIsLoaded(true),
          onError: () => setHasError(true)
        })}
      </Suspense>
    </LazyErrorBoundary>
  );
};

// Intersection Observer based lazy loading hook
export const useLazyLoad = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const ref = React.useRef();

  const defaultOptions = React.useMemo(() => ({
    threshold: 0.1,
    rootMargin: '50px',
    triggerOnce: true,
    ...options
  }), [options]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          setHasLoaded(true);
          if (defaultOptions.triggerOnce) {
            observer.disconnect();
          }
        } else if (!defaultOptions.triggerOnce) {
          setIsIntersecting(false);
        }
      },
      defaultOptions
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [defaultOptions]);

  return [ref, isIntersecting, hasLoaded];
};

// Progressive enhancement hook
export const useProgressiveEnhancement = (featureCheck, fallbackComponent = null) => {
  const [isSupported, setIsSupported] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const checkSupport = async () => {
      try {
        const supported = await featureCheck();
        setIsSupported(supported);
      } catch (error) {
        console.warn('Progressive enhancement check failed:', error);
        setIsSupported(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSupport();
  }, [featureCheck]);

  if (isLoading) {
    return { isSupported: false, isLoading: true, Component: LoadingFallback };
  }

  return {
    isSupported,
    isLoading: false,
    Component: isSupported ? null : fallbackComponent
  };
};

// Feature detection utilities
export const featureChecks = {
  // Service Worker support
  serviceWorker: () => 'serviceWorker' in navigator,

  // WebGL support
  webGL: () => {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        canvas.getContext('webgl'));
    } catch (e) {
      return false;
    }
  },

  // IndexedDB support
  indexedDB: () => 'indexedDB' in window,

  // Web Workers support
  webWorkers: () => 'Worker' in window,

  // Intersection Observer support
  intersectionObserver: () => 'IntersectionObserver' in window,

  // Performance API support
  performanceAPI: () => 'performance' in window && 'memory' in performance,

  // Local Storage support
  localStorage: () => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }
};

// Resource preloader for critical assets
export const useResourcePreloader = (resources = []) => {
  const [loadedResources, setLoadedResources] = React.useState(new Set());
  const [loadingProgress, setLoadingProgress] = React.useState(0);

  React.useEffect(() => {
    if (resources.length === 0) return;

    const loadResource = async (resource) => {
      try {
        if (resource.type === 'script') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = resource.url;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        } else if (resource.type === 'style') {
          await new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = resource.url;
            link.onload = resolve;
            link.onerror = reject;
            document.head.appendChild(link);
          });
        } else if (resource.type === 'image') {
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = resource.url;
          });
        }

        setLoadedResources(prev => new Set([...prev, resource.url]));
      } catch (error) {
        console.warn(`Failed to preload resource: ${resource.url}`, error);
      }
    };

    const loadAllResources = async () => {
      for (let i = 0; i < resources.length; i++) {
        await loadResource(resources[i]);
        setLoadingProgress(((i + 1) / resources.length) * 100);
      }
    };

    loadAllResources();
  }, [resources]);

  return {
    loadedResources: Array.from(loadedResources),
    loadingProgress,
    isComplete: loadedResources.size === resources.length
  };
};

// Initialize progressive loading on app start
export const initializeProgressiveLoading = () => {
  // Preload critical components
  preloadCriticalComponents();

  // Set up performance observer for lazy loading hints
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('[ProgressiveLoading] LCP:', entry.startTime);
          }
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('[ProgressiveLoading] Performance observer not supported');
    }
  }

  console.log('[ProgressiveLoading] Initialized progressive loading system');
};

// All exports are already defined above as export const statements
